'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState, useTransition } from 'react';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import cloneDeep from 'lodash/cloneDeep';
import { Product } from 'shared/types/product';
import { ProductQuery } from 'shared/types/query/ProductQuery';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import useI18n from 'helpers/hooks/useI18n';
import usePath from 'helpers/hooks/usePath';
import useSdk from 'helpers/hooks/useSdk';
import { Category } from 'types/entity/category';
import { LineItem as WishlistLineItem } from 'types/entity/wishlist';
import { useCart, useWishlist } from 'frontastic';
import { EditorialContentConfiguration, FacetConfiguration, PriceConfiguration, RangeFacet, TermFacet } from '../types';
import { refinementRemovedEventName, refinementsClearedEventName } from './constants';
import { ActiveRefinement, ProductListContextShape, RefinementRemovedEvent, Sort, UiState } from './types';

const buildSearchParams = (
  uiState: UiState,
  facetsConfiguration: Record<string, FacetConfiguration>,
  sort?: Sort,
  limit?: number,
  cursor?: string,
) => {
  const params = new URLSearchParams();

  if (uiState?.searchQuery) params.set('query', uiState.searchQuery);

  Object.values(facetsConfiguration).forEach((configuration) => {
    if (!configuration.selected) return;

    if (configuration.type === 'range') {
      params.set(`facets[${configuration.key}][min]`, (configuration.minSelected ?? configuration.min).toString());
      params.set(`facets[${configuration.key}][max]`, (configuration.maxSelected ?? configuration.max).toString());
    } else if (configuration.type === 'term') {
      if (configuration.key === 'categories.categoryId') {
        configuration.terms.filter((term) => term.selected).forEach((term) => params.append('categories[]', term.key));
      } else {
        configuration.terms
          .filter((term) => term.selected)
          .forEach((term, index) => params.set(`facets[${configuration.key}][terms][${index}]`, term.key));
      }
    }
  });

  if (sort && sort.attribute) params.set(`sortAttributes[0][${sort.attribute}]`, sort.value);

  if (cursor) params.set('cursor', cursor);
  if (limit) params.set('limit', limit.toString());

  return params;
};

export const ProductListContext = createContext<ProductListContextShape>({
  categories: [],
  pricesConfiguration: {},
  facetsConfiguration: {},
  totalItems: 0,
  activeRefinements: [],
  refine() {},
  refineRange() {},
  replaceSort() {},
  removeAllRefinements() {},
  loadMore() {},
  editorialContent: [],
  additionalItems: [],
  loading: false,
  navigating: false,
});

export interface ProductListPropsProps {
  uiState: UiState;
  categories: Category[];
  pricesConfiguration: Record<string, PriceConfiguration>;
  facetsConfiguration: Record<string, FacetConfiguration>;
  editorialContent: EditorialContentConfiguration[];
  originalQuery?: ProductQuery;
}

const limitStep = 16;

const ProductListProvider = ({
  children,
  categories,
  uiState: initialUiState,
  facetsConfiguration,
  pricesConfiguration,
  editorialContent,
  originalQuery,
}: React.PropsWithChildren<ProductListPropsProps>) => {
  const sdk = useSdk();
  const extensions = sdk.composableCommerce;

  const [uiState, setUiState] = useState(initialUiState || {});
  const [loading, setLoading] = useState(false);
  const [navigating, startTransition] = useTransition();
  const [additionalItems, setAdditionalItems] = useState<Product[]>([]);

  const router = useRouter();

  const { pathWithoutQuery } = usePath();

  const { locale } = useParams();

  const searchParams = useSearchParams();

  const limit = searchParams.get('limit');

  const { currency } = useI18n();

  const {
    addItem,
    shippingMethods: { data: shippingMethods },
  } = useCart();

  const { data: wishlist, addToWishlist, removeLineItem } = useWishlist();

  const activeSort = useMemo<Sort | undefined>(() => {
    for (const q of searchParams.entries()) {
      const match = q[0].match(/sortAttributes\[0\]\[(.+)\]/);

      if (match?.[1]) return { attribute: match[1], value: q[1] as 'asc' | 'desc' };
    }

    return { attribute: '', value: 'desc' }; //Relevance
  }, [searchParams]);

  const activeLimit = useMemo<number>(() => {
    return limit ? +limit : limitStep;
  }, [limit]);

  useEffect(() => {
    setAdditionalItems([]);
    setUiState(initialUiState || {});
  }, [initialUiState]);

  const applyRefinements = useCallback(
    (facetsConfiguration: Record<string, FacetConfiguration>, sort?: Sort, limit?: number, cursor?: string) => {
      const params = buildSearchParams(uiState, facetsConfiguration, sort, limit, cursor);
      setAdditionalItems([]);
      startTransition(() => router.push(`${pathWithoutQuery}?${params.toString()}`));
    },
    [uiState, router, pathWithoutQuery],
  );

  const activeRefinements = useMemo(() => {
    const refinements = [] as ActiveRefinement[];

    const newFacetsConfiguration = cloneDeep(facetsConfiguration);

    const addRefinement = (configuration: FacetConfiguration, label: string, refineCb: () => void) => {
      refinements.push({
        attribute: configuration.key,
        label,
        refine() {
          refineCb();

          applyRefinements(newFacetsConfiguration, activeSort, activeLimit);

          window.dispatchEvent(
            new CustomEvent<RefinementRemovedEvent>(refinementRemovedEventName, {
              detail: { attribute: configuration.key },
            }),
          );
        },
      });
    };

    Object.values(newFacetsConfiguration)
      .filter((configuration) => configuration.selected)
      .forEach((configuration) => {
        if (configuration.type === 'range') {
          addRefinement(
            configuration,
            `${CurrencyHelpers.formatForCurrency(
              configuration.minSelected ?? configuration.min,
              locale,
              currency,
            )} - ${CurrencyHelpers.formatForCurrency(
              configuration.maxSelected ?? configuration.max,
              locale,
              currency,
            )}`,
            () => {
              configuration.selected = false;
            },
          );
        } else if (configuration.type === 'term') {
          configuration.terms
            .filter((t) => t.selected)
            .forEach((term) => {
              addRefinement(configuration, term.label, () => {
                term.selected = false;
                configuration.selected = configuration.terms.some((t) => t.selected);
              });
            });
        }
      });

    return refinements;
  }, [currency, facetsConfiguration, locale, applyRefinements, activeSort, activeLimit]);

  const replaceSort = useCallback(
    (newSort: Sort) => {
      applyRefinements(facetsConfiguration, newSort, activeLimit);
    },
    [facetsConfiguration, applyRefinements, activeLimit],
  );

  const refine = useCallback(
    (attribute: string, key: string) => {
      const newFacetsConfiguration = cloneDeep(facetsConfiguration);

      const facet = newFacetsConfiguration[attribute] as TermFacet;

      const term = facet.terms.find((t) => t.key === key);

      if (term) {
        term.selected = !term.selected;
      }

      facet.selected = facet.terms.some((t) => t.selected);

      applyRefinements(newFacetsConfiguration, activeSort);
    },
    [facetsConfiguration, applyRefinements, activeSort],
  );

  const refineRange = useCallback(
    (attribute: string, value: [number, number]) => {
      const newFacetsConfiguration = cloneDeep(facetsConfiguration);

      const facet = newFacetsConfiguration[attribute] as RangeFacet;

      if (facet) {
        facet.minSelected = value[0];
        facet.maxSelected = value[1];
        facet.selected = true;
      }

      applyRefinements(newFacetsConfiguration, activeSort);
    },
    [facetsConfiguration, applyRefinements, activeSort],
  );

  const loadMore = useCallback(async () => {
    const query = {
      ...originalQuery,
      facets: Object.fromEntries(originalQuery?.facets?.map((x) => [x.identifier, x]) || []),
      sortAttributes: originalQuery?.sortAttributes
        ? Object.entries(originalQuery.sortAttributes).map((entry) => Object.fromEntries([entry]))
        : undefined,
      cursor: uiState?.nextCursor,
    };
    setLoading(true);
    const results = await extensions.product.search(query).finally(() => setLoading(false));
    if (!results.isError) {
      setUiState((current) => ({
        ...current,
        nextCursor: results.data.nextCursor,
        totalItems: results.data.total || 0,
      }));
      setAdditionalItems((current) => [...current, ...results.data.items]);
    }
  }, [extensions.product, originalQuery, uiState?.nextCursor]);

  const removeAllRefinements = useCallback(() => {
    const newFacetsConfiguration = cloneDeep(facetsConfiguration);

    Object.values(newFacetsConfiguration).forEach((configuration) => {
      configuration.selected = false;

      if (configuration.type === 'term') configuration.terms.forEach((t) => (t.selected = false));
    });

    applyRefinements(newFacetsConfiguration, activeSort);

    window.dispatchEvent(new CustomEvent(refinementsClearedEventName));
  }, [applyRefinements, facetsConfiguration, activeSort]);

  const addToWishlist2 = useCallback(
    async (lineItem: WishlistLineItem, count: number) => {
      if (wishlist) await addToWishlist(wishlist, lineItem, count);
    },
    [addToWishlist, wishlist],
  );
  const removeFromWishlist = useCallback(
    async (lineItem: WishlistLineItem) => {
      if (wishlist) await removeLineItem(wishlist, lineItem);
    },
    [removeLineItem, wishlist],
  );

  return (
    <ProductListContext.Provider
      value={{
        ...uiState,
        categories,
        shippingMethods,
        facetsConfiguration,
        pricesConfiguration,
        activeSort,
        activeLimit,
        activeRefinements,
        refine,
        refineRange,
        replaceSort,
        removeAllRefinements,
        loadMore,
        wishlist,
        addToWishlist: addToWishlist2,
        removeFromWishlist,
        onAddToCart: addItem,
        editorialContent,
        additionalItems,
        loading,
        navigating,
      }}
    >
      {children}
    </ProductListContext.Provider>
  );
};

export default ProductListProvider;

export const useProductList = () => useContext(ProductListContext);
